import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';

import {
  FindInPage as FindingIcon,
  Delete as DeleteIcon,
  NotInterested as IrrelevantIcon,
  CheckCircle as RelevantIcon,
  BugReport as RootCauseIcon,
  ZoomIn as DiveDeepIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { Finding } from '../../../types';
import DraggableItemsPanel from '../../../shared/components/DraggableItemsPanel';
import ItemCard, { ActionItem, StatusChip } from '../../../shared/components/ItemCard';
import { rankToDisplayNumber } from '../../../shared/utils/ranking';
import IrrelevantReasonDialog from './IrrelevantReasonDialog';

interface FindingsPanelProps {
  findings: Finding[];
  onViewFinding: (finding: Finding) => void;
  onRemoveFinding: (findingId: string) => void;
  onReorderFindings: (findings: Finding[]) => void;
  onToggleRelevance: (findingId: string, irrelevant: boolean) => void;
  onToggleRootCause: (findingId: string, isPotentialRootCause: boolean) => void;
  onDiveDeeper: (finding: Finding) => void;
  onMarkIrrelevant: (finding: Finding, reason: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const FindingsPanel: React.FC<FindingsPanelProps> = ({
  findings,
  onViewFinding,
  onRemoveFinding,
  onReorderFindings,
  onToggleRelevance,
  onToggleRootCause,
  onDiveDeeper,
  onMarkIrrelevant,
  collapsed,
  onToggleCollapse,
}) => {
  const [irrelevantDialogFinding, setIrrelevantDialogFinding] = useState<Finding | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    findings.forEach(f => f.tags?.forEach(t => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [findings]);

  const filteredFindings = useMemo(() => {
    if (selectedTags.length === 0) return findings;
    return findings.filter(f =>
      selectedTags.some(tag => f.tags?.includes(tag))
    );
  }, [findings, selectedTags]);

  const renderFinding = (finding: Finding) => {
    const statusChips: StatusChip[] = [];

    if (finding.potential_root_cause) {
      statusChips.push({
        label: 'Root Cause',
        color: 'error',
        icon: <RootCauseIcon sx={{ fontSize: '0.7rem' }} />,
      });
    }

    if (finding.irrelevant) {
      statusChips.push({
        label: 'Irrelevant',
        color: 'default',
      });
    }

    const actions: ActionItem[] = [
      {
        id: 'dive-deeper',
        label: 'Dive Deeper',
        icon: <DiveDeepIcon sx={{ fontSize: 16 }} />,
        onClick: () => onDiveDeeper(finding),
      },
      {
        id: 'root-cause',
        label: 'Toggle Root Cause',
        icon: <RootCauseIcon sx={{ fontSize: 16 }} />,
        onClick: () => onToggleRootCause(finding.id, !finding.potential_root_cause),
        conditional: {
          condition: finding.potential_root_cause || false,
          trueLabel: 'Unmark as Root Cause',
          falseLabel: 'Mark as Root Cause',
        },
      },
      {
        id: 'relevance',
        label: 'Toggle Relevance',
        icon: <IrrelevantIcon sx={{ fontSize: 16 }} />,
        onClick: () => {
          if (finding.irrelevant) {
            // Mark as Relevant - direct toggle
            onToggleRelevance(finding.id, false);
          } else {
            // Mark as Irrelevant - open dialog for reason
            setIrrelevantDialogFinding(finding);
          }
        },
        conditional: {
          condition: finding.irrelevant || false,
          trueLabel: 'Mark as Relevant',
          falseLabel: 'Mark as Irrelevant',
          trueIcon: <RelevantIcon sx={{ fontSize: 16 }} />,
          falseIcon: <IrrelevantIcon sx={{ fontSize: 16 }} />,
        },
      },
      {
        id: 'remove',
        label: 'Remove from Investigation',
        icon: <DeleteIcon sx={{ fontSize: 16 }} />,
        onClick: () => onRemoveFinding(finding.id),
        color: 'error.main',
      },
    ];

    return (
      <ItemCard
        key={finding.id}
        item={finding}
        rank={rankToDisplayNumber(finding.rank)}
        onViewClick={() => onViewFinding(finding)}
        renderContent={(f) => (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                overflowWrap: 'anywhere',
                mb: 0.5,
              }}
            >
              {f.title}
            </Typography>
            {f.summary && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {f.summary}
              </Typography>
            )}
            {f.tags && f.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {f.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    icon={<TagIcon sx={{ fontSize: '0.7rem' }} />}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
        statusChips={statusChips}
        dimmed={finding.irrelevant}
        strikethrough={finding.irrelevant}
        actionMode="menu"
        actions={actions}
      />
    );
  };

  return (
    <>
      <DraggableItemsPanel
        title="Investigation Findings"
        titleIcon={<FindingIcon color="secondary" />}
        subtitle="Critical discoveries and insights"
        items={filteredFindings}
        headerExtra={allTags.length > 0 ? (
          <Autocomplete
            multiple
            size="small"
            options={allTags}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value)}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => {
                const { key, ...rest } = getTagProps({ index });
                return <Chip key={key} label={tag} size="small" sx={{ height: 20, fontSize: '0.7rem' }} {...rest} />;
              })
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter by tag..." variant="outlined" />
            )}
            sx={{ width: '100%' }}
          />
        ) : undefined}
        emptyStateIcon={<FindingIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />}
        emptyStateText="No findings yet"
        emptyStateSubtext="Investigation findings will appear here as they are discovered"
        onReorderItems={onReorderFindings}
        renderItem={renderFinding}
        getItemId={(item) => item.id}
        droppableId="findings"
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      />

      <IrrelevantReasonDialog
        open={!!irrelevantDialogFinding}
        onClose={() => setIrrelevantDialogFinding(null)}
        onConfirm={onMarkIrrelevant}
        finding={irrelevantDialogFinding}
      />
    </>
  );
};

export default FindingsPanel;